import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import ThemeToggle from './ThemeToggle';
import LazyImage from './LazyImage';
import ExtensionInstallModal from './ExtensionInstallModal';

interface NavbarProps {
  onLogout: () => void;
  onLogin?: () => void;
  isAuthenticated?: boolean;
  onProfileEdit?: () => void;
  onVersionLog?: () => void;
  onSecretKeyManage?: () => void;
  onMediaManage?: () => void;
  onBillingCenter?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
  onLogout, 
  onLogin,
  isAuthenticated = false,
  onProfileEdit,
  onVersionLog,
  onSecretKeyManage,
  onMediaManage,
  onBillingCenter
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ username: string } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // 导航链接样式
  const getNavLinkClass = ({ isActive }: { isActive: boolean }) => 
    `px-3 lg:px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
      isActive
        ? 'bg-white dark:bg-gray-600 text-primary dark:text-white shadow-sm'
        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
    }`;

  // 移动端导航链接样式
  const getMobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `w-full text-left px-4 py-2 flex items-center space-x-2 ${
      isActive 
        ? 'text-primary bg-primary/10 dark:bg-primary/20' 
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`;

  // 微信分享功能
  const handleWechatShare = async () => {
    try {
      const shareText = `🚀 推荐一个我正在使用的AI提示词管理工具 - AI咖！

💎 三大核心功能，全方位提升AI使用效率：

🌐 【网站平台】
• 海量精品提示词库，覆盖各行业场景
• 私有提示词库，永久免费

🧩 【浏览器插件】
• 任意AI对话页面一键调用
• 数据同步，一处保存，随处使用



🌐 立即体验：https://www.aiprompter.cc
`;

      // 尝试使用现代剪贴板API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareText);
        toast.success('🎉 分享文案已复制！快去微信朋友圈分享AI咖吧！');
      } else {
        // 兼容性处理：使用传统方法
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.success('🎉 分享文案已复制！快去微信朋友圈分享AI咖吧！');
        } catch (err) {
          toast.error('复制失败，请手动复制分享链接');
          console.error('复制失败:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制分享链接');
      console.error('复制失败:', error);
    }
  };

  // 获取当前用户信息
  const fetchCurrentUser = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/user/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setCurrentUser({ username: data.data.username });
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  }, [isAuthenticated]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 当认证状态改变时获取用户信息
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentUser();
    } else {
      setCurrentUser(null);
    }
  }, [isAuthenticated, fetchCurrentUser]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 bg-opacity-80 dark:bg-opacity-80 backdrop-blur-md z-40 border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center space-x-6"
          >
            {/* Logo - 链接到首页 */}
            <Link to="/plaza" className="flex items-center space-x-2 sm:space-x-3">
              <LazyImage 
                src="/images/icons/icon128.png" 
                alt="AI咖 - AI提示词管理平台Logo" 
                className="w-8 sm:w-10 h-8 sm:h-10 rounded-xl object-contain bg-white"
                width={40}
                height={40}
                wrapperClassName="w-8 sm:w-10 h-8 sm:h-10"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">AI咖</span>
                <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-normal hidden sm:block">Prompt Manager</span>
              </div>
            </Link>

            {/* 页面导航 - 使用 NavLink */}
            <div className="hidden md:flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <NavLink to="/plaza" className={getNavLinkClass}>
                <i className="fas fa-globe mr-1 lg:mr-2"></i>
                <span className="hidden lg:inline">提示词广场</span>
                <span className="lg:hidden">广场</span>
              </NavLink>

              <NavLink to="/agent-market" className={getNavLinkClass}>
                <i className="fas fa-robot mr-1 lg:mr-2"></i>
                <span className="hidden lg:inline">智能体市场</span>
                <span className="lg:hidden">智能体</span>
              </NavLink>

              {isAuthenticated && (
                <>
                  <NavLink to="/dashboard" className={getNavLinkClass}>
                    <i className="fas fa-home mr-1 lg:mr-2"></i>
                    <span className="hidden lg:inline">我的提示词</span>
                    <span className="lg:hidden">我的</span>
                  </NavLink>
                  <NavLink to="/my-agents" className={getNavLinkClass}>
                    <i className="fas fa-robot mr-1 lg:mr-2"></i>
                    <span className="hidden lg:inline">我的智能体</span>
                    <span className="lg:hidden">智能体</span>
                  </NavLink>
                </>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center space-x-2 sm:space-x-4"
          >
            {/* 微信分享按钮 */}
            <button
              onClick={handleWechatShare}
              className="flex items-center justify-center w-10 h-10 text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 group"
              title="分享到微信"
            >
              <i className="fab fa-weixin text-lg group-hover:scale-110 transition-transform"></i>
            </button>

            {/* 主题切换按钮 */}
            <ThemeToggle />


            {/* 浏览器插件下载按钮 */}
            <button
              onClick={() => setShowExtensionModal(true)}
              className="hidden sm:flex items-center space-x-2 px-3 lg:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              <i className="fas fa-download"></i>
              <span className="hidden lg:inline">浏览器插件</span>
              <span className="lg:hidden">插件</span>
            </button>

            {/* 移动端汉堡菜单按钮 */}
            <div className="md:hidden relative" ref={mobileMenuRef}>
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="flex items-center justify-center w-10 h-10 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                title={showMobileMenu ? '关闭菜单' : '打开菜单'}
                aria-label={showMobileMenu ? '关闭菜单' : '打开菜单'}
              >
                <i className={`fas ${showMobileMenu ? 'fa-times' : 'fa-bars'} text-lg`}></i>
              </button>
              
              {/* 移动端菜单下拉 */}
              {showMobileMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                >
                  <NavLink
                    to="/plaza"
                    onClick={() => setShowMobileMenu(false)}
                    className={getMobileNavLinkClass}
                  >
                    <i className="fas fa-globe text-gray-400 dark:text-gray-500"></i>
                    <span>提示词广场</span>
                  </NavLink>

                  <NavLink
                    to="/agent-market"
                    onClick={() => setShowMobileMenu(false)}
                    className={getMobileNavLinkClass}
                  >
                    <i className="fas fa-robot text-gray-400 dark:text-gray-500"></i>
                    <span>智能体市场</span>
                  </NavLink>

                  {isAuthenticated && (
                    <>
                      <NavLink
                        to="/dashboard"
                        onClick={() => setShowMobileMenu(false)}
                        className={getMobileNavLinkClass}
                      >
                        <i className="fas fa-home text-gray-400 dark:text-gray-500"></i>
                        <span>我的提示词</span>
                      </NavLink>
                      <NavLink
                        to="/my-agents"
                        onClick={() => setShowMobileMenu(false)}
                        className={getMobileNavLinkClass}
                      >
                        <i className="fas fa-robot text-gray-400 dark:text-gray-500"></i>
                        <span>我的智能体</span>
                      </NavLink>
                    </>
                  )}
                  <hr className="my-1 border-gray-100 dark:border-gray-700" />
                  {/* 移动端微信分享按钮 */}
                  <button
                    onClick={() => {
                      handleWechatShare();
                      setShowMobileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <i className="fab fa-weixin text-green-500"></i>
                    <span>分享到微信</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowExtensionModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 sm:hidden"
                  >
                    <i className="fas fa-download text-gray-400 dark:text-gray-500"></i>
                    <span>浏览器插件</span>
                  </button>
                </motion.div>
              )}
            </div>

            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <i className="fas fa-user-circle text-lg"></i>
                  <span className="hidden sm:inline">{currentUser?.username || '我的账户'}</span>
                  <i className={`fas fa-chevron-down text-xs transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
                </button>
                
                {/* 用户菜单下拉 */}
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                  >
                    {onProfileEdit && (
                      <button
                        onClick={() => {
                          onProfileEdit();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <i className="fas fa-user-edit text-gray-400 dark:text-gray-500"></i>
                        <span>编辑资料</span>
                      </button>
                    )}
                    {onSecretKeyManage && (
                      <button
                        onClick={() => {
                          onSecretKeyManage();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <i className="fas fa-key text-gray-400 dark:text-gray-500"></i>
                        <span>Secret Key</span>
                      </button>
                    )}
                    {/* 账单中心 */}
                    {onBillingCenter && (
                      <button
                        onClick={() => {
                          onBillingCenter();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <i className="fas fa-wallet text-emerald-500"></i>
                        <span>账单中心</span>
                      </button>
                    )}
                    {/* 管理员专用 - 媒体管理 */}
                    {onMediaManage && currentUser?.username === 'admin' && (
                      <button
                        onClick={() => {
                          onMediaManage();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <i className="fas fa-photo-video text-orange-500"></i>
                        <span>媒体管理</span>
                      </button>
                    )}
                    <hr className="my-1 border-gray-100 dark:border-gray-700" />
                    <button
                      onClick={() => {
                        onLogout();
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <i className="fas fa-sign-out-alt text-red-400"></i>
                      <span>退出登录</span>
                    </button>
                  </motion.div>
                )}
              </div>
            ) : (
              onLogin && (
                <Link
                  to="/login"
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  <i className="fas fa-sign-in-alt"></i>
                  <span className="hidden sm:inline">登录</span>
                </Link>
              )
            )}
          </motion.div>
        </div>
      </div>
    </nav>
    
      {/* 插件安装弹框 */}
      <ExtensionInstallModal
        isOpen={showExtensionModal}
        onClose={() => setShowExtensionModal(false)}
      />
    </>
  );
};

export default Navbar;
